import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  siteName?: string;
  twitterCard?: string;
  twitterSite?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title = 'HCLTech + AWS: Engineering the Future of Business',
  description = 'Discover HCLTech\'s comprehensive AWS solutions, GTM initiatives, and success stories. Transform your business with our 30,000 AWS certifications and 19 AWS competencies.',
  keywords = 'HCLTech, AWS, cloud solutions, digital transformation, GTM initiatives, AWS partnership, cloud migration, modernization',
  image = '/images/hcltech-aws-og-image.jpg',
  url = 'https://team-portal.hcltech.com',
  type = 'website',
  siteName = 'HCLTech AWS EBU Portal',
  twitterCard = 'summary_large_image',
  twitterSite = '@HCLTech',
  author = 'HCLTech',
  publishedTime,
  modifiedTime
}) => {
  const fullTitle = title.includes('HCLTech') ? title : `${title} | HCLTech AWS Solutions`;
  const canonicalUrl = url;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="language" content="English" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:site" content={twitterSite} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Article Meta Tags (for blog posts) */}
      {type === 'article' && (
        <>
          <meta property="article:author" content={author} />
          {publishedTime && <meta property="article:published_time" content={publishedTime} />}
          {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
        </>
      )}
      
      {/* Additional SEO Meta Tags */}
      <meta name="theme-color" content="#1f2937" />
      <meta name="msapplication-TileColor" content="#1f2937" />
      <meta name="application-name" content={siteName} />
      
      {/* Preconnect to external domains */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      
      {/* DNS Prefetch */}
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="dns-prefetch" href="//fonts.gstatic.com" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "HCLTech",
          "url": "https://www.hcltech.com",
          "logo": "https://www.hcltech.com/sites/default/files/hcl_logo.png",
          "description": description,
          "sameAs": [
            "https://www.linkedin.com/company/hcltech",
            "https://twitter.com/HCLTech",
            "https://www.facebook.com/HCLTech"
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "customer service",
            "url": "https://www.hcltech.com/contact-us"
          }
        })}
      </script>
    </Helmet>
  );
};

export default SEOHead;